<template>
  <div>
    <!-- Back Button and Header -->
    <v-btn icon class="back-button" @click="openExitConfirmation">
      <v-icon>mdi-arrow-left</v-icon>
    </v-btn>
    <v-row justify="center" class="my-2">
      <h2 class="red--text font-weight-bold">Engineer Quote</h2>
    </v-row>

    <!-- Exit Confirmation Dialog -->
    <v-dialog v-model="exitDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Confirm Exit</v-card-title>
        <v-card-text>
          Are you sure you want to exit? Unsaved changes will be lost.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" @click="exitWithoutSaving">Exit</v-btn>
          <v-btn @click="exitDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Dialog -->
    <v-dialog v-model="dialogSuccess" max-width="500px">
      <v-card>
        <v-card-title class="headline">Quote Created Successfully</v-card-title>
        <v-card-text>
          Where would you like to go?
        </v-card-text>
        <v-card-actions>
          <v-btn color="green" @click="navigateTo('Functions')">Go to Function</v-btn>
          <v-btn color="blue" @click="navigateTo('AllQuote')">Go to All Quotes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog v-model="dialogError" max-width="500px">
      <v-card>
        <v-card-title class="headline">Submission Error</v-card-title>
        <v-card-text>
          There are missing required fields. Please review the form and fill in all the necessary information before submitting.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" @click="dialogError = false">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Generic Confirmation Dialog -->
    <v-dialog v-model="confirmDialog.show" max-width="500px">
      <v-card>
        <v-card-title class="headline">Confirm Action</v-card-title>
        <v-card-text>{{ confirmDialog.message }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green" @click="handleConfirm">Yes</v-btn>
          <v-btn color="grey" @click="closeConfirmDialog">No</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Main Form -->
    <v-card class="pa-6 add-job-card">
      <v-card-text>
        <h3 class="section-title font-weight-bold">Quote Details</h3>
        <v-form ref="form" v-model="valid">
          <v-row dense>
            <!-- Quote Number -->
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="quoteNumber"
                label="Quote Number"
                dense
                outlined
                prepend-inner-icon="mdi-cube-outline"
                class="montserrat-input"
                :rules="[rules.required]"
                readonly
              ></v-text-field>
            </v-col>

            <!-- Seal Type -->
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="jobName"
                label="Seal Type"
                dense
                outlined
                prepend-inner-icon="mdi-cube-outline"
                class="montserrat-input"
                :rules="[rules.required]"
              ></v-text-field>
            </v-col>

            <!-- Shaft Size -->
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="ShaftSize"
                label="Shaft Size"
                dense
                outlined
                prepend-inner-icon="mdi-clock"
                class="montserrat-input"
                :rules="[rules.required]"
                type="number"
                min="0"
                step="any"
              ></v-text-field>
            </v-col>

            <!-- Customer Name -->
            <v-col cols="12" md="3" class="pa-2">
              <v-autocomplete
                v-model="customerName"
                :items="customers"
                item-text="companyName"
                item-value="docId"
                label="Customer Name"
                dense
                outlined
                prepend-inner-icon="mdi-account"
                class="montserrat-input"
                clearable
                :rules="[rules.required]"
              ></v-autocomplete>
            </v-col>

            <!-- Contact Person -->
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="customerNumber"
                label="Contact Person"
                dense
                outlined
                prepend-inner-icon="mdi-account"
                class="montserrat-input"
                :rules="[rules.required]"
              ></v-text-field>
            </v-col>

            <!-- Engineer -->
            <v-col cols="12" md="3" class="pa-2">
              <v-select
                v-model="engineer"
                :items="engineers"
                label="Engineer"
                item-text="fullName"
                item-value="docId"
                outlined
                dense
                prepend-inner-icon="mdi-account"
                class="montserrat-input"
                :rules="[rules.required]"
              ></v-select>
            </v-col>

            <!-- Salesperson -->
            <v-col cols="12" md="3" class="pa-2">
              <v-select
                v-model="salesperson"
                :items="salespersons"
                label="Salesperson"
                item-text="fullName"
                item-value="docId"
                outlined
                dense
                prepend-inner-icon="mdi-account"
                class="montserrat-input"
                :rules="[rules.required]"
              ></v-select>
            </v-col>

            <!-- Priority -->
            <v-col cols="12" md="3" class="pa-2">
              <v-select
                v-model="urgency"
                :items="urgencyOptions"
                label="Priority"
                dense
                outlined
                prepend-inner-icon="mdi-alert"
                class="montserrat-input"
                :rules="[rules.required]"
              ></v-select>
            </v-col>

            <!-- Set Rate -->
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="setRate"
                label="Set Rate"
                dense
                outlined
                prepend-inner-icon="mdi-currency-usd"
                class="montserrat-input"
                readonly
              ></v-text-field>
            </v-col>

            <!-- Labour Hours -->
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="timeToComplete"
                label="Labour Hours"
                dense
                outlined
                prepend-inner-icon="mdi-clock"
                class="montserrat-input"
                :rules="[rules.required]"
                type="number"
                min="0"
                step="any"
                @input="calculateGrandTotal"
              ></v-text-field>
            </v-col>

            <!-- Status -->
            <v-col cols="12" md="3" class="pa-2">
              <v-select
                v-model="status"
                :items="statuses"
                label="Status"
                outlined
                dense
                prepend-inner-icon="mdi-timeline-clock"
                class="montserrat-input"
                @change="handleStatusChange"
                :rules="[rules.required]"
              ></v-select>
            </v-col>

            <!-- Quote Date -->
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="currentDate"
                label="Quote Date"
                dense
                outlined
                prepend-inner-icon="mdi-calendar"
                class="montserrat-input"
                readonly
              ></v-text-field>
            </v-col>

            <!-- Total Lead Time -->
            <v-col cols="12" md="3" class="pa-2">
              <v-select
                v-model="TotalLead"
                :items="leadTimeOptions"
                label="Total Lead Time"
                dense
                outlined
                prepend-inner-icon="mdi-clock"
                class="montserrat-input"
                :rules="[rules.required]"
              ></v-select>
            </v-col>

            <!-- Elastomers -->
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="elastomers"
                label="Elastomers"
                dense
                outlined
                class="montserrat-input"
                :rules="[rules.required]"
              ></v-text-field>
            </v-col>

            <!-- IBS Face Material -->
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="ibsFaceMaterial"
                label="IBS Face Material"
                dense
                outlined
                class="montserrat-input"
                :rules="[rules.required]"
              ></v-text-field>
            </v-col>

            <!-- IBR Face Material -->
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="ibrFaceMaterial"
                label="IBR Face Material"
                dense
                outlined
                class="montserrat-input"
                :rules="[rules.required]"
              ></v-text-field>
            </v-col>

            <!-- OBS Face Material -->
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="obsFaceMaterial"
                label="OBS Face Material"
                dense
                outlined
                prepend-inner-icon="mdi-cog"
                class="montserrat-input"
                :rules="[rules.required]"
              ></v-text-field>
            </v-col>

            <!-- OBR Face Material -->
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="obrFaceMaterial"
                label="OBR Face Material"
                dense
                outlined
                prepend-inner-icon="mdi-cog"
                class="montserrat-input"
                :rules="[rules.required]"
              ></v-text-field>
            </v-col>

            <!-- Conditional Fields -->
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-if="showInvoiceNumberField"
                v-model="invoiceNumber"
                label="Invoice Number"
                dense
                outlined
                prepend-inner-icon="mdi-file-document"
                class="montserrat-input"
                :rules="[rules.required]"
              ></v-text-field>

              <v-text-field
                v-if="showQuoteNumberField"
                v-model="quoteNumberAdditional"
                label="Quote Number (Optional)"
                dense
                outlined
                prepend-inner-icon="mdi-file-document"
                class="montserrat-input"
              ></v-text-field>

              <v-text-field
                v-if="showReasonField"
                v-model="cancellationReason"
                label="Reason for Cancellation"
                dense
                outlined
                prepend-inner-icon="mdi-alert"
                class="montserrat-input"
                :rules="[rules.required]"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Job Description -->
          <v-row>
            <v-col cols="12" class="pa-2">
              <v-textarea
                v-model="jobDescription"
                label="Job Description"
                outlined
                dense
                prepend-inner-icon="mdi-format-list-bulleted"
                class="montserrat-input"
                style="min-height: 100px;"
                :rules="[rules.required]"
              ></v-textarea>
            </v-col>
          </v-row>

          <!-- Labour Parts as Table -->
          <v-row class="my-4">
            <h2 class="red--text mx-12">Labour Parts</h2>
          </v-row>
          <div class="table-responsive">
            <v-simple-table class="pa-1 ma-0">
              <thead>
                <tr>
                  <th class="text-left">Qty</th>
                  <th class="text-left">Part Size</th>
                  <th class="text-left">Part Description</th>
                  <th class="text-left">Item Category</th>
                  <th class="text-left">Mark Up</th>
                  <th class="text-left">Unit Price</th>
                  <th class="text-left">Total Price</th>
                  <th class="text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(labourItem, index) in labourItems" :key="index">
                  <td>
                    <v-text-field
                      v-model="labourItem.quantity"
                      dense
                      outlined
                      class="montserrat-input w-100"
                      :rules="[rules.required, rules.nonNegative]"
                      type="number"
                      min="0"
                      step="any"
                      @input="calculateLabourTotalPrice(labourItem)"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-text-field
                      v-model="labourItem.partSize"
                      label="Part Size"
                      dense
                      outlined
                      class="montserrat-input w-100"
                      :rules="[rules.required, rules.nonNegative]"
                      type="number"
                      min="0"
                      step="1"
                      @input="onPartSizeInput(labourItem)"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-text-field
                      v-model="labourItem.description"
                      label="Part Description"
                      dense
                      outlined
                      class="montserrat-input w-100"
                      :rules="[rules.required]"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-select
                      v-model="labourItem.jobCategory"
                      :items="jobCategories.labour"
                      label="Item Category"
                      dense
                      outlined
                      class="montserrat-input w-100"
                      :rules="[rules.required]"
                      @change="setMarkup(labourItem)"
                    ></v-select>
                  </td>
                  <td>
                    <v-text-field
                      v-model="labourItem.markUp"
                      label="Mark Up"
                      dense
                      outlined
                      class="montserrat-input w-100"
                      :rules="[rules.required, rules.nonNegative]"
                      :readonly="labourItem.jobCategory !== 'Custom'"
                      type="number"
                      min="0"
                      step="any"
                      @input="calculateLabourTotalPrice(labourItem)"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-text-field
                      v-model="labourItem.unitPrice"
                      label="Unit Price"
                      dense
                      outlined
                      class="montserrat-input w-100"
                      :rules="[rules.required, rules.nonNegative]"
                      type="number"
                      min="0"
                      step="any"
                      :readonly="labourItem.jobCategory !== 'Custom'"
                      @input="calculateLabourTotalPrice(labourItem)"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-text-field
                      v-model="labourItem.totalPrice"
                      label="Total Price"
                      dense
                      outlined
                      class="montserrat-input w-100"
                      :rules="[rules.required, rules.nonNegative]"
                      readonly
                      type="number"
                      min="0"
                      step="any"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-btn color="red" @click="confirmRemoveLabourItem(index)" style="margin-bottom: 30px;">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <v-row justify="start" class="pa-2">
            <v-btn dark color="blue" @click="confirmAddLabourItem" class="ma-2">
              <v-icon left>mdi-plus</v-icon>Add Additional Fields
            </v-btn>
          </v-row>

          <!-- Labour Sub Total -->
          <v-row justify="end" class="my-2">
            <h2 class="red--text">Labour Sub Total</h2>
          </v-row>
          <v-row justify="end">
            <v-col cols="12" md="3">
              <v-text-field
                v-model="labourSubTotal"
                label="Labour Sub Total"
                dense
                outlined
                prepend-inner-icon="mdi-cash-multiple"
                class="montserrat-input"
                readonly
                type="number"
                min="0"
                step="any"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Stock Parts as Table -->
          <v-row class="my-4">
            <h2 class="red--text mx-12">Stock Parts</h2>
          </v-row>
          <div class="table-responsive">
            <v-simple-table class="pa-1 ma-0">
              <thead>
                <tr>
                  <th class="text-left">Qty</th>
                  <th class="text-left">Code</th>
                  <th class="text-left">Additional Info</th>
                  <th class="text-left">Lead Time</th>
                  <th class="text-left">Item Category</th>
                  <th class="text-left" v-if="!isTechnician">Cost Price</th>
                  <th class="text-left" v-if="!isTechnician">Mark Up</th>
                  <th class="text-left">Unit Price</th>
                  <th class="text-left">Total Price</th>
                  <th class="text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(stockItem, index) in stockItems" :key="index">
                  <td>
                    <v-text-field
                      v-model="stockItem.quantity"
                      dense
                      outlined
                      class="montserrat-input w-100"
                      :rules="[rules.required, rules.nonNegative]"
                      type="number"
                      min="0"
                      step="any"
                      @input="calculateStockTotalPrice(stockItem)"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-text-field
                      v-model="stockItem.code"
                      label="Code"
                      dense
                      outlined
                      class="montserrat-input w-100"
                      :rules="[rules.required]"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-btn color="blue" @click="openSupplierPopup(index)" style="margin-bottom: 30px;">
                      <v-icon>mdi-plus</v-icon>Add
                    </v-btn>
                  </td>
                  <td>
                    <v-select
                      v-model="stockItem.leadTime"
                      :items="leadTimeOptions"
                      label="Lead Time"
                      dense
                      outlined
                      class="montserrat-input w-100"
                      :rules="[rules.required]"
                    ></v-select>
                  </td>
                  <td>
                    <v-select
                      v-model="stockItem.jobCategory"
                      :items="jobCategories.stock"
                      label="Item Category"
                      dense
                      outlined
                      class="montserrat-input w-100"
                      :rules="[rules.required]"
                      @change="setMarkup(stockItem)"
                    ></v-select>
                  </td>
                  <td v-if="!isTechnician">
                    <v-text-field
                      v-model="stockItem.costPrice"
                      label="Cost Price"
                      dense
                      outlined
                      class="montserrat-input w-100"
                      :rules="[rules.required, rules.nonNegative]"
                      type="number"
                      min="0"
                      step="any"
                      @input="calculateStockTotalPrice(stockItem)"
                    ></v-text-field>
                  </td>
                  <td v-if="!isTechnician">
                    <v-text-field
                      v-model="stockItem.markUp"
                      label="Mark Up"
                      dense
                      outlined
                      class="montserrat-input w-100"
                      :rules="[rules.required, rules.nonNegative]"
                      :readonly="stockItem.jobCategory !== 'Custom'"
                      type="number"
                      min="0"
                      step="any"
                      @input="calculateStockTotalPrice(stockItem)"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-text-field
                      v-model="stockItem.unitPrice"
                      label="Unit Price"
                      dense
                      outlined
                      class="montserrat-input w-100"
                      :rules="[rules.required, rules.nonNegative]"
                      type="number"
                      min="0"
                      step="any"
                      @input="calculateStockTotalPrice(stockItem)"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-text-field
                      v-model="stockItem.totalPrice"
                      label="Total Price"
                      dense
                      outlined
                      class="montserrat-input w-100"
                      :rules="[rules.required, rules.nonNegative]"
                      readonly
                      type="number"
                      min="0"
                      step="any"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-btn color="red" @click="confirmRemoveStockItem(index)" style="margin-bottom: 30px;">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <v-row justify="start" class="pa-2">
            <v-btn dark color="blue" @click="confirmAddStockItem" class="ma-2">
              <v-icon left>mdi-plus</v-icon>Add Additional Fields
            </v-btn>
          </v-row>

          <!-- Stock Sub Total -->
          <v-row justify="end" class="my-2">
            <h2 class="red--text">Stock Sub Total</h2>
          </v-row>
          <v-row justify="end">
            <v-col cols="12" md="3">
              <v-text-field
                v-model="stockSubTotal"
                label="Stock Sub Total"
                dense
                outlined
                prepend-inner-icon="mdi-cash-multiple"
                class="montserrat-input"
                readonly
                type="number"
                min="0"
                step="any"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Grand Total -->
          <v-row justify="end" class="my-2">
            <h2 class="red--text">Grand Total</h2>
          </v-row>
          <v-row justify="end">
            <v-col cols="12" md="3">
              <v-text-field
                v-model="grandTotal"
                label="Grand Total"
                dense
                outlined
                prepend-inner-icon="mdi-cash-multiple"
                class="montserrat-input"
                readonly
                type="number"
                min="0"
                step="any"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Supplier Quotes Section -->
          <v-row class="my-4">
            <h2 class="red--text mx-12">Supplier Quotes</h2>
          </v-row>
          <div class="mx-5">
            <v-row
              v-for="(supplierQuote, index) in supplierQuotes"
              :key="index"
              class="mb-2 extra-info-item"
            >
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="supplierQuote.quoteText"
                  label="Supplier Quote"
                  outlined
                  dense
                  class="montserrat-input"
                  :rules="[rules.required]"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <div class="supplier-quote-icons" style="display: flex; align-items: center; gap: 8px;">
                  <v-btn color="primary" icon @click="triggerSupplierQuoteUpload(index)">
                    <v-icon>mdi-upload</v-icon>
                  </v-btn>
                  <input
                    type="file"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    :ref="'supplierQuoteFileInput' + index"
                    style="display: none;"
                    @change="uploadSupplierQuotePhoto(index, $event)"
                  />
                  <v-btn v-if="supplierQuote.photoUrl" color="success" icon @click="openViewDialog(supplierQuote.photoUrl)">
                    <v-icon>mdi-eye</v-icon>
                  </v-btn>
                  <v-btn color="red" icon @click="confirmRemoveSupplierQuote(index)">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                  <v-btn color="blue" icon @click="confirmAddSupplierQuote">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </div>
              </v-col>
              <v-col cols="12" md="6" class="d-flex justify-end">
                <v-img 
                  v-if="supplierQuote.photoUrl" 
                  :src="supplierQuote.photoUrl" 
                  alt="Uploaded Photo" 
                  max-width="150"
                ></v-img>
              </v-col>
            </v-row>
          </div>

          <!-- Additional Fields -->
          <v-row class="my-4">
            <h2 class="red--text mx-12">Additional Fields</h2>
          </v-row>
          <v-row justify="start" class="pa-2 mx-5">
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="sampleNumber"
                label="Sample Number (Optional)"
                outlined
                dense
               
                class="montserrat-input"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="customerReference"
                label="Customer Reference (Optional)"
                outlined
                dense
             
                class="montserrat-input"
              ></v-text-field>
            </v-col>
            <!-- New Quote Checked By Field -->
            <v-col cols="12" md="3" class="pa-2">
              <v-autocomplete
                v-model="quoteCheckedBy"
                :items="users"
                item-text="fullName"
                item-value="docId"
                label="Quote checked by"
                outlined
                dense
                class="montserrat-input"
                clearable
                :rules="[rules.quoteCheckedByRequired]"
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="3" class="pa-2">
              <v-text-field
                v-model="dateQuoted"
                label="Date Quoted (Auto)"
                outlined
                dense
                class="montserrat-input"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Comments -->
          <v-row class="my-4">
            <v-col cols="12" class="pa-2">
              <v-textarea
                v-model="comment"
                label="Comment"
                outlined
                dense
                class="montserrat-input mx-5"
                style="min-height: 100px;"
              ></v-textarea>
            </v-col>
          </v-row>

          <!-- Submit Button -->
          <v-row justify="center" class="my-6">
            <v-btn color="blue" large @click="submitQuote" class="montserrat-button">
              <v-icon left>mdi-briefcase-plus</v-icon>Create Quote
            </v-btn>
          </v-row>
        </v-form>

        <!-- Loading Overlay -->
        <v-overlay :value="loading">
          <v-progress-circular size="100" color="white" indeterminate></v-progress-circular>
        </v-overlay>
      </v-card-text>
    </v-card>

    <!-- Snackbars for Success and Error Messages -->
    <v-snackbar v-model="successSnack.show" :timeout="3000" color="green" top right>
      {{ successSnack.message }}
      <v-btn text @click="successSnack.show = false">Close</v-btn>
    </v-snackbar>

    <v-snackbar v-model="errorSnack.show" :timeout="3000" color="red" top right>
      {{ errorSnack.message }}
      <v-btn text @click="errorSnack.show = false">Close</v-btn>
    </v-snackbar>

    <!-- View Image Dialog -->
    <v-dialog v-model="viewDialog" max-width="800px">
      <v-card>
        <v-card-title class="headline">View Image</v-card-title>
        <v-card-text>
          <img :src="currentViewImage" alt="Uploaded Image" style="width: 100%;" />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue" @click="viewDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Supplier Popup Dialog -->
    <v-dialog v-model="supplierPopup.dialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Supplier Details</v-card-title>
        <v-card-text>
          <v-form ref="supplierForm" v-model="supplierPopup.valid">
            <v-autocomplete
              v-model="supplierPopup.supplier"
              :items="suppliers"
              item-text="supplierName"
              item-value="docId"
              label="Supplier"
              dense
              outlined
              class="montserrat-input"
              :rules="[rules.required]"
            ></v-autocomplete>
            <v-text-field
              v-model="supplierPopup.drawingNumber"
              label="Drawing Number"
              dense
              outlined
              class="montserrat-input"
            ></v-text-field>
            <v-textarea
              v-model="supplierPopup.specialNote"
              label="Special Note"
              dense
              outlined
              class="montserrat-input"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green" @click="saveSupplierDetails">Save</v-btn>
          <v-btn @click="closeSupplierPopup">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  limit,
  doc,
  getDoc,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { firebaseApp } from "@/firebaseConfig";

export default {
  name: "EngineerQuote",
  data() {
    return {
      // Dialogs
      exitDialog: false,
      dialogSuccess: false,
      dialogError: false,

      // Generic Confirmation Dialog
      confirmDialog: {
        show: false,
        message: "",
        onConfirm: null,
      },

      // Form Data
      valid: false,
      quoteNumber: "",
      jobName: "",
      ShaftSize: "",
      customerName: "",
      customerNumber: "",
      engineer: null,
      // Technician field removed
      salesperson: null,
      urgency: "",
      setRate: "",
      timeToComplete: "",
      status: "",
      currentDate: new Date().toISOString().split("T")[0],
      TotalLead: "",
      invoiceNumber: "",
      quoteNumberAdditional: "",
      salesOrderNumber: "",
      cancellationReason: "",
      jobDescription: "",
      sampleNumber: "",
      customerReference: "",
      dateQuoted: "",
      comment: "",
      quoteCheckedBy: null, // New field for Quote checked by
      grandTotal: 0,
      labourSubTotal: 0,
      stockSubTotal: 0,
      previousStatus: '',

      // Arrays
      labourItems: [
        {
          quantity: 1,
          description: "",
          partSize: 0,
          markUp: 0,
          unitPrice: 0,
          totalPrice: 0,
          jobCategory: "",
        },
      ],
      stockItems: [
        {
          quantity: 1,
          code: "",
          leadTime: "",
          jobCategory: "",
          costPrice: 0,
          markUp: 0,
          unitPrice: 0,
          totalPrice: 0,
          supplier: "",
          drawingNumber: "",
          specialNote: "",
        },
      ],
      supplierQuotes: [
        {
          quoteText: "",
          photo: null,
          photoUrl: "",
        },
      ],
      extraInfoFields: [],

      // Suppliers and Users
      users: [],
      engineers: [],
      // technicians removed
      salespersons: [],
      customers: [],
      suppliers: [],

      // Loading
      loading: false,

      // Supplier Popup
      supplierPopup: {
        dialog: false,
        index: null,
        supplier: null,
        drawingNumber: "",
        specialNote: "",
        valid: false,
      },

      // Snackbars
      successSnack: {
        show: false,
        message: "",
      },
      errorSnack: {
        show: false,
        message: "",
      },

      // Rules
      rules: {
        required: (value) => !!value || "Required.",
        maxLength: (length) => (value) =>
          (value && value.length <= length) || `Max ${length} characters`,
        nonNegative: (value) =>
          (value !== null && value !== undefined && value >= 0) || "Must be non-negative.",
        quoteCheckedByRequired: (value) => {
          if (this.status === 'Quoted' && this.previousStatus === 'Costing Complete') {
            return !!value || 'Quote checked by is required when moving from Costing Complete to Quoted.';
          }
          return true;
        },
      },

      // State
      isTechnician: false,

      // Job Categories
      jobCategories: {
        stock: [
          "Bearings",
          "Bolts/Nuts",
          "Coating (Any)",
          "Die Set",
          "Gasket",
          "Grinding - no drawing",
          "Lip Seal / V-seal (not in seal)",
          "Lip Seal / V-seal (in seal or pump repair)",
          "Local Mods",
          "Machining - no drawing",
          "Material",
          "Nexus Drawn item",
          "O-Rings Only Quote",
          "O-Rings with seal",
          "Special Markup",
          "Stock Item New",
          "Tools",
          "Custom",
        ],
        labour: [
          "Lapping Carbon",
          "Lapping Sic/SSic/SS/Cer",
          "Lapping TC",
          "Custom",
        ],
      },

      // Lead Time Options (dropdown for the stock parts lead time and total lead)
      leadTimeOptions: [
        "1-2 DAYS",
        "2-3 DAYS",
        "3-5 DAYS",
        "4-6 DAYS",
        "5-7 DAYS",
        "6-8 DAYS",
        "8-10 DAYS",
        "10-12 DAYS",
        "10-15 DAYS",
        "15-20 DAYS",
        "6-8 WEEKS",
        "8-10 WEEKS",
        "10-12 WEEKS",
        "Ex - Stock",
        "Custom",
      ],

      // Dropdown Items
      urgencyOptions: ["Urgent", "Medium", "Not Urgent"],
      statuses: [
        "Inspection",
        "Drawing",
        "Costing Complete",
        "Quoted",
        "Completed",
        "Canceled",
      ],

      // View Dialog
      viewDialog: false,
      currentViewImage: "",
      elastomers: '',
      ibsFaceMaterial: '',
      ibrFaceMaterial: '',
      obsFaceMaterial: '',
      obrFaceMaterial: '',
    };
  },

  created() {
    this.generateQuoteNumber();
    this.fetchUsers();
    this.fetchCustomers();
    this.fetchSuppliers();
    this.fetchSetRate();
    this.checkUserRole();
  },

  computed: {
    showQuoteNumberField() {
      return this.status === "Quoted";
    },
    showSalesOrderField() {
      return (
        this.status === "Inprogress" ||
        this.status === "Ready" ||
        this.status === "Delayed"
      );
    },
    showInvoiceNumberField() {
      return this.status === "Completed";
    },
    showReasonField() {
      return this.status === "Canceled";
    },
  },

  watch: {
    // Recalculate overall totals whenever labour or stock items change
    labourItems: {
      handler() {
        this.labourItems.forEach((item) => {
          this.calculateLabourTotalPrice(item);
        });
        this.calculateGrandTotal();
      },
      deep: true,
    },
    stockItems: {
      handler() {
        this.stockItems.forEach((item) => {
          this.calculateStockTotalPrice(item);
        });
        this.calculateGrandTotal();
      },
      deep: true,
    },
    supplierQuotes: {
      handler() {
        // Process supplierQuotes changes if needed
      },
      deep: true,
    },
  },

  methods: {
    // Generic Confirmation Dialog
    showConfirmDialog(message, onConfirm) {
      this.confirmDialog.message = message;
      this.confirmDialog.onConfirm = onConfirm;
      this.confirmDialog.show = true;
    },
    handleConfirm() {
      if (this.confirmDialog.onConfirm) {
        this.confirmDialog.onConfirm();
      }
      this.closeConfirmDialog();
    },
    closeConfirmDialog() {
      this.confirmDialog.show = false;
      this.confirmDialog.message = "";
      this.confirmDialog.onConfirm = null;
    },

    // Exit Confirmation
    openExitConfirmation() {
      this.showConfirmDialog(
        "Are you sure you want to exit? Unsaved changes will be lost.",
        this.exitWithoutSaving
      );
    },
    exitWithoutSaving() {
      this.$router.go(-1);
    },

    // Fetching Data
    async fetchSetRate() {
      try {
        if (localStorage.getItem("setRate")) {
          this.setRate = localStorage.getItem("setRate");
        } else {
          const db = getFirestore(firebaseApp);
          const rateDoc = await getDoc(doc(db, "rate", "currentRate"));
          if (rateDoc.exists()) {
            this.setRate = rateDoc.data().rate;
            localStorage.setItem("setRate", this.setRate);
          }
        }
      } catch (error) {
        console.error("Error fetching set rate:", error);
      }
    },
    async fetchCustomers() {
      try {
        const db = getFirestore(firebaseApp);
        const customersCollection = collection(db, "customers");
        const customersSnapshot = await getDocs(customersCollection);
        this.customers = customersSnapshot.docs.map((doc) => ({
          docId: doc.id,
          companyName: doc.data()["Company Name"] || "",
          contactPerson: doc.data()["Contact Person"] || "",
          customerAccount: doc.data()["Customer Account"] || "",
        }));
        localStorage.setItem("customers", JSON.stringify(this.customers));
      } catch (error) {
        console.error("Error fetching customers:", error);
      }
    },
    async fetchSuppliers() {
      try {
        const db = getFirestore(firebaseApp);
        const suppliersCollection = collection(db, "suppliers");
        const suppliersSnapshot = await getDocs(suppliersCollection);
        this.suppliers = suppliersSnapshot.docs.map((doc) => ({
          docId: doc.id,
          supplierName: doc.data()["Supplier Name"] || "",
          contactPerson: doc.data()["Contact Person"] || "",
          supplierAccount: doc.data()["Supplier Account"] || "",
        }));
        localStorage.setItem("suppliers", JSON.stringify(this.suppliers));
      } catch (error) {
        console.error("Error fetching suppliers:", error);
      }
    },
    async fetchUsers() {
      try {
        const db = getFirestore(firebaseApp);
        const usersCollection = collection(db, "users");
        const usersSnapshot = await getDocs(usersCollection);
        const usersData = usersSnapshot.docs.map((doc) => ({
          docId: doc.id,
          ...doc.data(),
        }));

        this.users = usersData;
        localStorage.setItem("users", JSON.stringify(this.users));

        this.engineers = usersData
          .filter((user) => user.role === "Engineer")
          .sort((a, b) => a.fullName.localeCompare(b.fullName));
        // technicians field removed
        this.salespersons = usersData
          .filter((user) => user.role === "Salesperson")
          .sort((a, b) => a.fullName.localeCompare(b.fullName));
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    },

    // User Role Management
    getLoggedInUserRole() {
      return localStorage.getItem("role") || "Admin";
    },
    checkUserRole() {
      const role = this.getLoggedInUserRole();
      this.isTechnician = role === "Technician";
    },

    // Add/Remove Labour Items
    confirmRemoveLabourItem(index) {
      this.showConfirmDialog(
        "Are you sure you want to remove this labour item?",
        () => this.removeLabourItem(index)
      );
    },
    confirmAddLabourItem() {
      this.showConfirmDialog(
        "Are you sure you want to add a new labour item?",
        this.addLabourItemConfirmed
      );
    },
    addLabourItemConfirmed() {
      this.labourItems.push({
        quantity: 1,
        description: "",
        partSize: 0,
        markUp: 0,
        unitPrice: 0,
        totalPrice: 0,
        jobCategory: "",
      });
    },
    removeLabourItem(index) {
      this.labourItems.splice(index, 1);
    },

    // Add/Remove Stock Items
    confirmRemoveStockItem(index) {
      this.showConfirmDialog(
        "Are you sure you want to remove this stock item?",
        () => this.removeStockItem(index)
      );
    },
    confirmAddStockItem() {
      this.showConfirmDialog(
        "Are you sure you want to add a new stock item?",
        this.addStockItemConfirmed
      );
    },
    addStockItemConfirmed() {
      this.stockItems.push({
        quantity: 1,
        code: "",
        leadTime: "",
        jobCategory: "",
        costPrice: 0,
        markUp: 0,
        unitPrice: 0,
        totalPrice: 0,
        supplier: "",
        drawingNumber: "",
        specialNote: "",
      });
    },
    removeStockItem(index) {
      this.stockItems.splice(index, 1);
    },

    // Add/Remove Supplier Quotes
    confirmAddSupplierQuote() {
      this.showConfirmDialog(
        "Are you sure you want to add a new supplier quote?",
        this.addSupplierQuoteConfirmed
      );
    },
    addSupplierQuoteConfirmed() {
      this.supplierQuotes.push({
        quoteText: "",
        photo: null,
        photoUrl: "",
      });
    },
    confirmRemoveSupplierQuote(index) {
      this.showConfirmDialog(
        "Are you sure you want to remove this supplier quote?",
        () => this.removeSupplierQuote(index)
      );
    },
    removeSupplierQuote(index) {
      this.supplierQuotes.splice(index, 1);
    },

    // Trigger File Upload for Supplier Quote
    triggerSupplierQuoteUpload(index) {
      this.$refs[`supplierQuoteFileInput${index}`][0].click();
    },
    async uploadSupplierQuotePhoto(index, event) {
      const file = event.target.files[0];
      if (file) {
        this.supplierQuotes[index].photo = file;
        try {
          const storage = getStorage(firebaseApp);
          const storageRef = ref(
            storage,
            `supplier-quotes/${this.quoteNumber}_quote_${index}_${file.name}`
          );
          await uploadBytes(storageRef, file);
          const downloadURL = await getDownloadURL(storageRef);
          this.supplierQuotes[index].photoUrl = downloadURL;
          this.successSnack.message = "Supplier quote photo uploaded successfully.";
          this.successSnack.show = true;
        } catch (error) {
          console.error("Error uploading supplier quote photo:", error);
          this.errorSnack.message = "Failed to upload supplier quote photo.";
          this.errorSnack.show = true;
        }
      }
    },

    // Calculation Methods

    // For Labour Parts: If not Custom, unitPrice = partSize * markUp (readonly), totalPrice = qty * unitPrice. If Custom, unitPrice is editable, totalPrice = qty * unitPrice.
    calculateLabourTotalPrice(labourItem) {
      const quantity = parseFloat(labourItem.quantity) || 0;
      if (labourItem.jobCategory !== 'Custom') {
        const partSize = parseFloat(labourItem.partSize) || 0;
        const markUp = parseFloat(labourItem.markUp) || 0;
        labourItem.unitPrice = partSize * markUp;
      }
      const unitPrice = parseFloat(labourItem.unitPrice) || 0;
      labourItem.totalPrice = Math.ceil(quantity * unitPrice);
      this.calculateGrandTotal();
    },

    // For Stock Parts:
    // If costPrice is provided and jobCategory is not "Custom", auto-calculate unitPrice as Math.round(costPrice * markUp).
    // Then totalPrice is Math.round(quantity * unitPrice).
    calculateStockTotalPrice(stockItem) {
      const quantity = parseFloat(stockItem.quantity) || 0;
      const costPrice = parseFloat(stockItem.costPrice) || 0;
      const markUp = parseFloat(stockItem.markUp) || 0;
      let unitPrice = parseFloat(stockItem.unitPrice) || 0;
      if (stockItem.costPrice) {
        if (stockItem.jobCategory !== "Custom") {
          unitPrice = Math.ceil(costPrice * markUp);
          stockItem.unitPrice = unitPrice;
        }
      }
      stockItem.totalPrice = Math.ceil(quantity * unitPrice);
      this.calculateGrandTotal();
    },

    // Calculate Overall Grand Total
    calculateGrandTotal() {
      const labourItemsTotal = this.labourItems.reduce(
        (acc, item) => acc + (parseFloat(item.totalPrice) || 0),
        0
      );
      const timeToCompleteTotal = parseFloat(this.timeToComplete) || 0;
      const setRateValue = parseFloat(this.setRate) || 0;
      const additionalLabourCost = timeToCompleteTotal * setRateValue;
      this.labourSubTotal = Math.ceil(labourItemsTotal + additionalLabourCost);

      this.stockSubTotal = Math.ceil(this.stockItems.reduce(
        (acc, item) => acc + (parseFloat(item.totalPrice) || 0),
        0
      ));
      this.grandTotal = Math.ceil(this.labourSubTotal + this.stockSubTotal);
    },

    // When the job category changes, update the markup value and recalc the item totals.
    setMarkup(item) {
      if (item.jobCategory === "Custom") {
        item.markUp = 1;
      } else {
        switch (item.jobCategory) {
          case "Lapping Carbon":
            item.markUp = 4.0;
            break;
          case "Lapping Sic/SSic/SS/Cer":
            item.markUp = 4.5;
            break;
          case "Lapping TC":
            item.markUp = 5.5;
            break;
          case "Bearings":
            item.markUp = 1.1;
            break;
          case "Bolts/Nuts":
            item.markUp = 1.5;
            break;
          case "Coating (Any)":
            item.markUp = 1.1;
            break;
          case "Die Set":
            item.markUp = 1.1;
            break;
          case "Gasket":
            item.markUp = 1.5;
            break;
          case "Grinding - no drawing":
            item.markUp = 1.5;
            break;
          case "Lip Seal / V-seal (not in seal)":
            item.markUp = 1.5;
            break;
          case "Lip Seal / V-seal (in seal or pump repair)":
            item.markUp = 1.1;
            break;
          case "Local Mods":
            item.markUp = 1.5;
            break;
          case "Machining - no drawing":
            item.markUp = 1.5;
            break;
          case "Material":
            item.markUp = 1.0;
            break;
          case "Nexus Drawn item":
            item.markUp = 1.8;
            break;
          case "O-Rings Only Quote":
            item.markUp = 3.0;
            break;
          case "O-Rings with seal":
            item.markUp = 1.5;
            break;
          case "Special Markup":
            item.markUp = 1.5;
            break;
          case "Stock Item New":
            item.markUp = 1.2;
            break;
          case "Tools":
            item.markUp = 1.1;
            break;
          default:
            item.markUp = 1.1;
            break;
        }
      }
      this.calculateLabourTotalPrice(item);
    },

    // Handle Status Change
    handleStatusChange() {
      this.previousStatus = this.status;
      if (this.status === "Quoted") {
        this.dateQuoted = new Date().toISOString().split("T")[0];
      } else {
        this.dateQuoted = "";
      }
      if (!this.showSalesOrderField) this.salesOrderNumber = "";
      if (!this.showInvoiceNumberField) this.invoiceNumber = "";
      if (!this.showReasonField) this.cancellationReason = "";
      if (!this.showQuoteNumberField) this.quoteNumberAdditional = "";
    },

    // Submit Quote (called directly on button click)
    async submitQuote() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        try {
          const db = getFirestore(firebaseApp);
          const storage = getStorage(firebaseApp);
          const selectedCustomer = this.customers.find(
            (customer) => customer.docId === this.customerName
          );
          const extraInfoData = await Promise.all(
            this.extraInfoFields.map(async (extraInfo, index) => {
              let fileUrl = null;
              if (extraInfo.file) {
                const storageRef = ref(
                  storage,
                  `extra-info/${this.quoteNumber}_extra_${index}_${extraInfo.file.name}`
                );
                await uploadBytes(storageRef, extraInfo.file);
                fileUrl = await getDownloadURL(storageRef);
              }
              return {
                text: extraInfo.text,
                fileUrl: fileUrl || "",
              };
            })
          );
          const stockItemsWithSupplier = this.stockItems.map((item) => ({
            ...item,
            supplierName: this.suppliers.find(
              (supplier) => supplier.docId === item.supplier
            )?.supplierName || "",
          }));
          const supplierQuotesData = this.supplierQuotes.map((q) => ({
            quoteText: q.quoteText,
            photoUrl: q.photoUrl,
          }));
          const quoteData = {
            quoteNumber: this.quoteNumber || "",
            jobName: this.jobName || "",
            customerName: selectedCustomer ? selectedCustomer.docId : "",
            customerNumber: this.customerNumber || "",
            engineer: this.engineers.find((user) => user.docId === this.engineer) || null,
            // technician field removed
            salesperson: this.salespersons.find((user) => user.docId === this.salesperson) || null,
            status: this.status || "",
            urgency: this.urgency || "",
            timeToComplete: this.timeToComplete || "",
            setRate: parseFloat(this.setRate) || 0,
            quoteNumberAdditional: this.showQuoteNumberField ? this.quoteNumberAdditional : null,
            salesOrderNumber: this.showSalesOrderField ? this.salesOrderNumber : null,
            invoiceNumber: this.showInvoiceNumberField ? this.invoiceNumber : null,
            cancellationReason: this.showReasonField ? this.cancellationReason : null,
            labourItems: this.labourItems,
            stockItems: stockItemsWithSupplier,
            supplierQuotes: supplierQuotesData,
            TotalLead: this.TotalLead || "",
            ShaftSize: this.ShaftSize || "",
            elastomers: this.elastomers || "",
            ibsFaceMaterial: this.ibsFaceMaterial || "",
            ibrFaceMaterial: this.ibrFaceMaterial || "",
            obsFaceMaterial: this.obsFaceMaterial || "",
            obrFaceMaterial: this.obrFaceMaterial || "",
            comment: this.comment || "",
            grandTotal: this.grandTotal || 0,
            labourSubTotal: this.labourSubTotal || 0,
            stockSubTotal: this.stockSubTotal || 0,
            sampleNumber: this.sampleNumber || "",
            customerReference: this.customerReference || "",
            dateQuoted: this.dateQuoted || "",
            currentDate: this.currentDate || new Date().toISOString().split("T")[0],
            extraInfoFields: extraInfoData,
            jobDescription: this.jobDescription || "",
            quoteCheckedBy: this.quoteCheckedBy || "",
          };

          await addDoc(collection(db, "EngineerQuotes"), quoteData);
          console.log("Quote created successfully");
          this.successSnack.message = "Quote saved successfully.";
          this.successSnack.show = true;
          this.dialogSuccess = true;
          this.resetForm();
        } catch (error) {
          console.error("Error creating quote:", error);
          this.errorSnack.message = "Failed to create quote.";
          this.errorSnack.show = true;
        } finally {
          this.loading = false;
        }
      } else {
        this.dialogError = true;
        console.log("Form validation failed");
      }
    },
    navigateTo(route) {
      this.dialogSuccess = false;
      this.$router.push({ path: `/${route}` });
    },

    // Extra Info Fields
    addExtraInfoField() {
      this.extraInfoFields.push({
        text: "",
        file: null,
        fileUrl: "",
      });
    },
    triggerExtraInfoFileUpload(index) {
      this.$refs.extraInfoFileInput[index].click();
    },
    async handleExtraInfoFileUpload(event, index) {
      const file = event.target.files[0];
      if (file) {
        this.extraInfoFields[index].file = file;
        try {
          const storage = getStorage(firebaseApp);
          const storageRef = ref(
            storage,
            `extra-info/${this.quoteNumber}_extra_${index}_${file.name}`
          );
          await uploadBytes(storageRef, file);
          const downloadURL = await getDownloadURL(storageRef);
          this.$set(this.extraInfoFields[index], "fileUrl", downloadURL);
          this.successSnack.message = "File uploaded successfully.";
          this.successSnack.show = true;
        } catch (error) {
          console.error("Error uploading file:", error);
          this.errorSnack.message = "Failed to upload file.";
          this.errorSnack.show = true;
        }
      }
    },
    openViewDialog(imageUrl) {
      this.currentViewImage = imageUrl;
      this.viewDialog = true;
    },
    removeExtraInfoField(index) {
      this.extraInfoFields.splice(index, 1);
    },

    // Supplier Popup
    openSupplierPopup(index) {
      this.supplierPopup.dialog = true;
      this.supplierPopup.index = index;
      this.supplierPopup.supplier = this.stockItems[index].supplier;
      this.supplierPopup.drawingNumber = this.stockItems[index].drawingNumber;
      this.supplierPopup.specialNote = this.stockItems[index].specialNote;
    },
    closeSupplierPopup() {
      this.supplierPopup.dialog = false;
      this.supplierPopup.index = null;
      this.supplierPopup.supplier = null;
      this.supplierPopup.drawingNumber = "";
      this.supplierPopup.specialNote = "";
    },
    async saveSupplierDetails() {
      if (this.$refs.supplierForm.validate()) {
        const index = this.supplierPopup.index;
        this.stockItems[index].supplier = this.supplierPopup.supplier;
        this.stockItems[index].drawingNumber = this.supplierPopup.drawingNumber;
        this.stockItems[index].specialNote = this.supplierPopup.specialNote;
        this.supplierPopup.dialog = false;
        this.supplierPopup.index = null;
        this.supplierPopup.supplier = null;
        this.supplierPopup.drawingNumber = "";
        this.supplierPopup.specialNote = "";
        this.successSnack.message = "Supplier details have been updated successfully.";
        this.successSnack.show = true;
      } else {
        this.errorSnack.message = "Supplier is required.";
        this.errorSnack.show = true;
      }
    },

    // Generate Quote Number Automatically
    async generateQuoteNumber() {
      try {
        const db = getFirestore(firebaseApp);
        const quotesCollection = collection(db, "EngineerQuotes");
        const q = query(quotesCollection, orderBy("quoteNumber", "desc"), limit(1));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const lastQuote = querySnapshot.docs[0].data();
          const lastNumberStr = lastQuote.quoteNumber.replace("EQ", "");
          const lastNumber = parseInt(lastNumberStr, 10);
          const newNumber = isNaN(lastNumber) ? 1 : lastNumber + 1;
          if (newNumber > 9999) {
            this.errorSnack.message = "Maximum number of quotes reached (EQ9999).";
            this.errorSnack.show = true;
            this.quoteNumber = "EQ9999";
            return;
          }
          this.quoteNumber = "EQ" + String(newNumber).padStart(4, "0");
        } else {
          this.quoteNumber = "EQ0001";
        }
      } catch (error) {
        console.error("Error generating quote number:", error);
        this.quoteNumber = "EQ0001";
      }
    },

    // Reset Form After Submission
    resetForm() {
      this.$refs.form.reset();
      this.labourItems = [
        {
          quantity: 1,
          description: "",
          partSize: 0,
          markUp: 0,
          unitPrice: 0,
          totalPrice: 0,
          jobCategory: "",
        },
      ];
      this.stockItems = [
        {
          quantity: 1,
          code: "",
          leadTime: "",
          jobCategory: "",
          costPrice: 0,
          markUp: 0,
          unitPrice: 0,
          totalPrice: 0,
          supplier: "",
          drawingNumber: "",
          specialNote: "",
        },
      ];
      this.supplierQuotes = [
        {
          quoteText: "",
          photo: null,
          photoUrl: "",
        },
      ];
      this.extraInfoFields = [];
      this.labourSubTotal = 0;
      this.stockSubTotal = 0;
      this.grandTotal = 0;
      this.generateQuoteNumber();
      this.dateQuoted = "";
      this.quoteNumberAdditional = "";
      this.salesOrderNumber = "";
      this.invoiceNumber = "";
      this.cancellationReason = "";
      this.jobDescription = "";
      this.urgency = "";
      this.quoteCheckedBy = null;
    },

    // Add a method to force integer part size
    onPartSizeInput(labourItem) {
      let val = String(labourItem.partSize);
      // Remove any non-digit characters and decimals
      val = val.replace(/[^\d]/g, '');
      labourItem.partSize = val ? parseInt(val, 10) : 0;
      this.calculateLabourTotalPrice(labourItem);
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap");

/* General Styling */
.full-page {
  position: relative;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
}

.add-job-card {
  position: relative;
  z-index: 2;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  width: 100%;
  padding: 20px;
  transition: all 0.3s ease-in-out;
  background-color: #ffffff;
}

.card-header {
  border-radius: 8px;
  background: linear-gradient(145deg, #ff3b47, #5a1014);
  box-shadow: 10px 10px 20px #1b1c1e, -10px -10px 20px #c4242b;
  padding: 20px;
  text-align: center;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon {
  margin-right: 10px;
  font-size: 24px;
}

.header-text {
  color: #991d23;
  font-size: 1.6rem;
  font-weight: bold;
}

.montserrat-title {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
}

.montserrat-input {
  font-family: "Montserrat", sans-serif;
  font-weight: 400;
}

.montserrat-button {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
}

.montserrat-alert {
  font-family: "Montserrat", sans-serif;
  font-weight: 400;
}

/* Back Button */
.back-button {
  color: #000000;
  position: absolute;
  top: 20px;
  left: 20px;
  transition: transform 0.3s ease-in-out;
}
.back-button:hover {
  transform: scale(1.1);
}
.back-button .v-icon {
  color: #000000;
}

/* Margin and Padding Utilities */
.my-2 {
  margin-top: 8px;
  margin-bottom: 8px;
}
.my-4 {
  margin-top: 16px;
  margin-bottom: 16px;
}
.my-6 {
  margin-top: 24px;
  margin-bottom: 24px;
}
.ma-1 {
  margin: 4px;
}
.ma-2 {
  margin: 8px;
}

/* Table Headers */
.v-simple-table th {
  background-color: #f5f5f5;
  color: #000000;
  padding: 12px;
}

/* Table Cells */
.v-simple-table td {
  padding: 12px;
}

/* Responsive Adjustments */
@media (max-width: 600px) {
  .add-job-card {
    padding: 10px;
  }

  .montserrat-input {
    font-size: 14px;
  }

  .montserrat-button {
    font-size: 14px;
  }
}

/* Extra Info Item Alignment */
.extra-info-item {
  align-items: center;
}

/* Responsive Table Wrapper */
.table-responsive {
  overflow-x: auto;
}

.table-responsive .v-simple-table {
  width: 100%;
  min-width: 800px;
}

/* Supplier Quotes Icons */
.upload-icon {
  font-size: 24px;
}
.view-icon {
  font-size: 24px;
  cursor: pointer;
}
.supplier-quote-icons {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
